/**
 * POST-INSTALL CONTENT — Settings section (Seth 2026-08-20: "Where in
 * settings will I be able to change and customize any of the post install
 * info being sent to customers. Like if a city changes their process of
 * scheduling inspections.")
 *
 * CITY-LIB-02 (2026-10-06): this now edits `pegasus_city_permit_info` — the
 * ONE city table the close-out recap and the permit email actually read
 * (inspector contact, phone, inspection window, how the ELECTRICAL side is
 * handled per city, and the uploaded city letter the email links to).
 * Seeded from the whole Drive pre-install letter library (126 cities,
 * CITY-LIB-01). Change a city here once and every future send is right.
 * The old `pegasus_inspection_contact` editor is retired — that table stays
 * in the database as legacy reference, but nothing sends from it.
 *
 * SETTINGS-NAV-02 (2026-09-23): the review / survey / private-feedback links
 * and the referral offer line are edited here too (PostInstallLinksCard) —
 * the post-install messages and the Inbox's review-link insert read them.
 */
import { useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Landmark, Plus, Pencil, Check, X, Loader2, Link2, AlertTriangle,
  FileText, UploadCloud, Zap, Phone,
} from "lucide-react";
import { useSaveOrgSetting } from "@/lib/orgSettings";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { InfoTip } from "@/components/InfoTip";
import { toastError } from "@/components/ui/mutationToast";

const useCanSettings = () => useCan("settings.manage");

// ---------------------------------------------------------------------------
// CITY-LIB-02 — the city inspection library (pegasus_city_permit_info).
// ---------------------------------------------------------------------------

type ElecMode = "onsite_closed" | "customer_calls" | "unknown";

interface CityRow {
  city: string;
  display_name: string;
  inspector_line: string;
  phone: string | null;
  window_days: number;
  extra_note: string | null;
  drive_file_id: string | null;
  electrical_mode: ElecMode;
  electrical_line: string | null;
  letter_url: string | null;
}

function useCityRows() {
  return useQuery({
    queryKey: ["city_permit_info"],
    queryFn: async (): Promise<CityRow[]> => {
      // Throw on failure — a broken read must NOT render as a calm empty list.
      const { data, error } = await supabase.from("pegasus_city_permit_info")
        .select("city, display_name, inspector_line, phone, window_days, extra_note, drive_file_id, electrical_mode, electrical_line, letter_url")
        .order("display_name");
      if (error) throw error;
      return (data ?? []) as CityRow[];
    },
  });
}

function useSaveCityRow() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: Partial<CityRow> & { city: string; isNew?: boolean }) => {
      const { city, isNew, ...patch } = v;
      if (isNew) {
        const { error } = await supabase.from("pegasus_city_permit_info").insert({
          city: city.trim().toLowerCase(),
          display_name: v.display_name ?? city.trim(),
          inspector_line: v.inspector_line ?? "your city's inspections department",
          window_days: v.window_days ?? 90,
          electrical_mode: v.electrical_mode ?? "unknown",
          updated_by: "settings",
        });
        if (error) throw error;
      } else {
        const { error } = await supabase.from("pegasus_city_permit_info")
          .update({ ...patch, updated_by: "settings", updated_at: new Date().toISOString() })
          .eq("city", city);
        if (error) throw error;
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["city_permit_info"] }),
  });
}

const needsAttention = (r: CityRow) =>
  (r.extra_note ?? "").includes("WARN:") || r.electrical_mode === "unknown" || !r.phone;

// LETTER-PAGE-01: every city has a PUBLIC letter page generated from its row
// (/letter/<slug>) — the recap links it automatically, nothing to upload.
const citySlug = (city: string) => city.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

export function PostInstallSection() {
  return (
    <div className="space-y-4">
      <PostInstallLinksCard />
      <CityInspectionsCard />
    </div>
  );
}

function CityInspectionsCard() {
  const { data: rows, isError: loadFailed } = useCityRows();
  const save = useSaveCityRow();
  const canManage = useCanSettings();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [onlyAttention, setOnlyAttention] = useState(false);
  const [editCity, setEditCity] = useState<string | null>(null);
  const [draft, setDraft] = useState<Partial<CityRow>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState("");
  const [busyCity, setBusyCity] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const uploadFor = useRef<CityRow | null>(null);

  const attentionCount = useMemo(() => (rows ?? []).filter(needsAttention).length, [rows]);
  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    return (rows ?? []).filter((r) =>
      (!onlyAttention || needsAttention(r)) &&
      (!ql || r.display_name.toLowerCase().includes(ql) || r.city.includes(ql) || (r.inspector_line ?? "").toLowerCase().includes(ql)),
    );
  }, [rows, q, onlyAttention]);

  const startEdit = (r: CityRow) => {
    setEditCity(r.city);
    setDraft({
      display_name: r.display_name, inspector_line: r.inspector_line, phone: r.phone ?? "",
      window_days: r.window_days, electrical_mode: r.electrical_mode,
      electrical_line: r.electrical_line ?? "", extra_note: r.extra_note ?? "",
    } as Partial<CityRow>);
  };

  // The letter the recap's "View your city's full inspection letter" button
  // opens. PDF strongly preferred — a .docx downloads instead of viewing.
  const uploadLetter = async (list: FileList | null) => {
    const r = uploadFor.current;
    const f = list?.[0];
    if (!r || !f) return;
    setBusyCity(r.city);
    try {
      const ext = (f.name.split(".").pop() || "pdf").toLowerCase();
      const path = `city-letters/${r.city.replace(/[^a-z0-9]+/g, "-")}.${ext}`;
      const { error } = await supabase.storage.from("email-assets")
        .upload(path, f, { upsert: true, contentType: f.type || undefined, cacheControl: "3600" });
      if (error) throw error;
      const { data: pub } = supabase.storage.from("email-assets").getPublicUrl(path);
      // Cache-bust so a replaced letter shows fresh.
      const url = `${pub.publicUrl}?v=${Date.now()}`;
      await save.mutateAsync({ city: r.city, letter_url: url });
      void qc.invalidateQueries({ queryKey: ["city_permit_info"] });
    } catch (e) {
      toastError((e as Error).message || "Upload failed.");
    } finally {
      setBusyCity(null);
      uploadFor.current = null;
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
        <Landmark className="h-4 w-4 text-slate-400" /> City Inspection Library
        <InfoTip label="About the city library">
          The close-out recap and the permit email are built from this list: the city&rsquo;s inspection contact
          and phone, the inspection window (used to compute the real deadline date), and how the ELECTRICAL side
          is handled (Powerhouse closes it on-site in most cities). Every city also gets a public LETTER PAGE
          generated from its row — the email&rsquo;s &ldquo;View your city&rsquo;s letter&rdquo; button links it
          automatically, so there is nothing to upload. Attaching the city&rsquo;s official PDF is optional; when
          present, the page links it too. Change a city here once and every future send is right.
        </InfoTip>
        <span className="text-xs font-medium text-slate-400">{(rows ?? []).length} cities</span>
      </h3>

      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city or inspector…"
          className="w-56 rounded-md border border-hairline px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30" />
        <button type="button" onClick={() => setOnlyAttention((v) => !v)}
          className={cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[13px] font-semibold transition-colors",
            onlyAttention ? "bg-amber-500 text-white shadow-sm" : "bg-amber-50 text-amber-700 hover:bg-amber-100")}>
          <AlertTriangle className="h-3.5 w-3.5" /> Needs a look ({attentionCount})
        </button>
        {canManage && (
          <button type="button" onClick={() => setAddOpen((v) => !v)}
            className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-800">
            <Plus className="h-4 w-4" /> Add City
          </button>
        )}
      </div>

      {loadFailed && (
        <div className="rounded-lg border border-rose-200 bg-rose-50/60 p-3 text-sm text-rose-700">
          The city library failed to load — this is NOT an empty list. Refresh; if it persists, tell Claude.
        </div>
      )}

      {addOpen && canManage && (
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-hairline bg-slate-50/60 p-3">
          <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="City name (as the address reads)"
            className="w-64 rounded-md border border-hairline px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none" autoFocus />
          <button type="button" disabled={!addName.trim() || save.isPending}
            onClick={() => {
              const key = addName.trim().toLowerCase();
              const dup = (rows ?? []).find((r) => r.city === key);
              if (dup) { startEdit(dup); setAddOpen(false); return; }
              save.mutate({ city: addName.trim(), display_name: addName.trim(), isNew: true }, {
                onSuccess: () => { setAddOpen(false); setAddName(""); setEditCity(key); setDraft({ display_name: addName.trim(), inspector_line: "", phone: "", window_days: 90, electrical_mode: "unknown", electrical_line: "", extra_note: "" } as Partial<CityRow>); },
                onError: (e) => toastError((e as Error).message),
              });
            }}
            className="rounded-md bg-brand-700 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50">
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}
          </button>
          <span className="text-xs text-slate-400">Matching is by the service address&rsquo;s city, so spell it the way addresses do.</span>
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-hairline bg-white">
        <ul className="divide-y divide-slate-100">
          {filtered.slice(0, 200).map((r) => (
            <li key={r.city} className="px-4 py-2.5">
              {editCity === r.city ? (
                <div className="space-y-2">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Display name</span>
                      <input value={(draft.display_name as string) ?? ""} onChange={(e) => setDraft((d) => ({ ...d, display_name: e.target.value }))}
                        className="mt-0.5 w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none" />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Inspector phone</span>
                      <input value={(draft.phone as string) ?? ""} onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
                        className="mt-0.5 w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none" />
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Who the customer calls (mechanical final)</span>
                    <input value={(draft.inspector_line as string) ?? ""} onChange={(e) => setDraft((d) => ({ ...d, inspector_line: e.target.value }))}
                      className="mt-0.5 w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none" />
                  </label>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <label className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Inspection window (days)</span>
                      <input type="number" min={1} value={Number(draft.window_days ?? 90)}
                        onChange={(e) => setDraft((d) => ({ ...d, window_days: Number(e.target.value) || 90 }))}
                        className="mt-0.5 w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm tabular-nums focus:border-brand-500 focus:outline-none" />
                    </label>
                    <label className="block">
                      <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Electrical inspection</span>
                      <select value={(draft.electrical_mode as string) ?? "unknown"}
                        onChange={(e) => setDraft((d) => ({ ...d, electrical_mode: e.target.value as ElecMode }))}
                        className="mt-0.5 w-full rounded-md border border-hairline bg-white px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none">
                        <option value="onsite_closed">Closed on-site by Powerhouse — customer does nothing</option>
                        <option value="customer_calls">Customer schedules the electrical too</option>
                        <option value="unknown">Not sure yet</option>
                      </select>
                    </label>
                  </div>
                  <label className="block">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Electrical wording (what the email says)</span>
                    <textarea rows={2} value={(draft.electrical_line as string) ?? ""} onChange={(e) => setDraft((d) => ({ ...d, electrical_line: e.target.value }))}
                      className="mt-0.5 w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm leading-snug focus:border-brand-500 focus:outline-none" />
                  </label>
                  <label className="block">
                    <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Office note (quirks — 48h notice, email-only, warnings)</span>
                    <textarea rows={2} value={(draft.extra_note as string) ?? ""} onChange={(e) => setDraft((d) => ({ ...d, extra_note: e.target.value }))}
                      className="mt-0.5 w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm leading-snug focus:border-brand-500 focus:outline-none" />
                  </label>
                  <div className="flex items-center gap-2">
                    <button type="button" disabled={save.isPending}
                      onClick={() => save.mutate({
                        city: r.city,
                        display_name: ((draft.display_name as string) ?? r.display_name).trim() || r.display_name,
                        inspector_line: ((draft.inspector_line as string) ?? "").trim() || r.inspector_line,
                        phone: ((draft.phone as string) ?? "").trim() || null,
                        window_days: Number(draft.window_days ?? r.window_days) || 90,
                        electrical_mode: (draft.electrical_mode as ElecMode) ?? r.electrical_mode,
                        electrical_line: ((draft.electrical_line as string) ?? "").trim() || null,
                        extra_note: ((draft.extra_note as string) ?? "").trim() || null,
                      }, { onSuccess: () => setEditCity(null), onError: (e) => toastError((e as Error).message) })}
                      className="inline-flex items-center gap-1 rounded-md bg-brand-700 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-brand-800 disabled:opacity-50">
                      <Check className="h-3.5 w-3.5" /> Save
                    </button>
                    <button type="button" onClick={() => setEditCity(null)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-slate-400 hover:text-slate-600">
                      <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-3">
                  <span className="w-44 shrink-0 pt-0.5">
                    <span className="block truncate text-sm font-semibold text-slate-800">{r.display_name}</span>
                    <span className="mt-0.5 flex flex-wrap items-center gap-1">
                      <ElecChip mode={r.electrical_mode} />
                      <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">{r.window_days}d</span>
                      {(r.extra_note ?? "").includes("WARN:") && (
                        <span className="inline-flex items-center gap-0.5 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700">
                          <AlertTriangle className="h-2.5 w-2.5" /> check
                        </span>
                      )}
                    </span>
                  </span>
                  <span className="min-w-0 flex-1 text-sm text-slate-600">
                    <span className="block truncate">{r.inspector_line}</span>
                    <span className="mt-0.5 flex items-center gap-2 text-xs text-slate-400">
                      {r.phone
                        ? <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>
                        : <span className="font-medium text-amber-600">no phone on file</span>}
                      {r.extra_note && <span className="truncate">{r.extra_note}</span>}
                    </span>
                  </span>
                  <span className="flex shrink-0 items-center gap-1 pt-0.5">
                    {/* The customer-facing letter PAGE — generated from this row, always there. */}
                    <a href={`/letter/${citySlug(r.city)}`} target="_blank" rel="noreferrer"
                      title="The customer letter page — generated from this row, linked automatically from the close-out email"
                      className="inline-flex items-center gap-1 rounded-lg bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700 hover:bg-emerald-100">
                      <FileText className="h-3 w-3" /> Letter Page
                    </a>
                    {r.letter_url && (
                      <a href={r.letter_url} target="_blank" rel="noreferrer" title="The city's official attachment (linked from the letter page)"
                        className="rounded-lg bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-200">
                        PDF
                      </a>
                    )}
                    {canManage && (
                      <>
                        <button type="button" disabled={busyCity === r.city}
                          title="Optional: attach the city's OFFICIAL letter/PDF — the page above already works without it"
                          onClick={() => { uploadFor.current = r; fileRef.current?.click(); }}
                          className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50">
                          {busyCity === r.city ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UploadCloud className="h-3.5 w-3.5" />}
                        </button>
                        <button type="button" onClick={() => startEdit(r)} title="Edit"
                          className="rounded-lg p-1.5 text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600">
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      </>
                    )}
                  </span>
                </div>
              )}
            </li>
          ))}
          {filtered.length === 0 && !loadFailed && <li className="px-4 py-6 text-center text-sm text-slate-400">No cities match.</li>}
        </ul>
        {filtered.length > 200 && (
          <div className="border-t border-slate-100 px-4 py-2 text-center text-xs text-slate-400">Showing 200 of {filtered.length} — narrow the search.</div>
        )}
      </div>
      <input ref={fileRef} type="file" accept=".pdf,.docx" className="hidden" onChange={(e) => void uploadLetter(e.target.files)} />
    </div>
  );
}

function ElecChip({ mode }: { mode: ElecMode }) {
  if (mode === "onsite_closed") return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
      <Zap className="h-2.5 w-2.5" /> elec closed on-site
    </span>
  );
  if (mode === "customer_calls") return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-sky-50 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">
      <Zap className="h-2.5 w-2.5" /> customer schedules elec
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-500">
      <Zap className="h-2.5 w-2.5" /> elec unknown
    </span>
  );
}

/**
 * SETTINGS-NAV-02: the links the post-install messages and the Inbox use —
 * org_setting review_link / survey_link / feedback_link / referral_terms had
 * no screen (the review-link hint in the Inbox said "add one in Settings" and
 * there was nowhere to). Reviews ON → the survey page + the review ask;
 * reviews OFF (the tech's quiet switch or the customer's preference) → the
 * private feedback page.
 */
const LINK_KEYS = ["review_link", "survey_link", "feedback_link", "referral_terms"] as const;
type LinkKey = (typeof LINK_KEYS)[number];
const LINK_FIELDS: { key: LinkKey; label: string; hint: string; placeholder: string }[] = [
  { key: "review_link", label: "Review link", hint: "The Google or Podium review page, used by the Inbox quick-insert and the review ask.", placeholder: "https://…" },
  { key: "survey_link", label: "Survey link", hint: "Sent after an install when a review ask is OK.", placeholder: "https://www.sedgwickheating.com/survey/" },
  { key: "feedback_link", label: "Private feedback link", hint: "Sent instead of the survey when reviews are switched off.", placeholder: "https://www.sedgwickheating.com/feedback/" },
  { key: "referral_terms", label: "Referral offer", hint: "The line in the referral message.", placeholder: "Refer a friend: $50 for you, $50 off for them." },
];

function useLinkSettings() {
  return useQuery({
    queryKey: ["org_setting", "post_install_links"],
    queryFn: async (): Promise<Record<LinkKey, string>> => {
      const { data, error } = await supabase.from("org_setting").select("key, value").in("key", [...LINK_KEYS]);
      if (error) throw error;
      const out = { review_link: "", survey_link: "", feedback_link: "", referral_terms: "" } as Record<LinkKey, string>;
      for (const r of (data ?? []) as { key: LinkKey; value: unknown }[]) out[r.key] = typeof r.value === "string" ? r.value : "";
      return out;
    },
  });
}

function PostInstallLinksCard() {
  const canManage = useCanSettings();
  const qc = useQueryClient();
  const { data } = useLinkSettings();
  const save = useSaveOrgSetting();
  const [draft, setDraft] = useState<Partial<Record<LinkKey, string>>>({});
  const [saved, setSaved] = useState(false);
  const val = (k: LinkKey) => draft[k] ?? data?.[k] ?? "";
  const dirty = LINK_KEYS.filter((k) => draft[k] !== undefined && draft[k]!.trim() !== (data?.[k] ?? ""));
  const onSave = async () => {
    for (const k of dirty) await save.mutateAsync({ key: k, value: draft[k]!.trim() });
    await Promise.all([
      qc.invalidateQueries({ queryKey: ["org_setting", "post_install_links"] }),
      qc.invalidateQueries({ queryKey: ["composer_extras"] }),
    ]);
    setDraft({});
    setSaved(true);
  };
  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
        <Link2 className="h-4 w-4 text-slate-400" /> Review, Survey &amp; Feedback Links
      </h3>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        {LINK_FIELDS.map((f) => (
          <div key={f.key}>
            <div className="mb-1 flex items-center gap-1.5">
              <label htmlFor={`post-install-${f.key}`} className="text-[12px] font-semibold text-slate-600">{f.label}</label>
              <InfoTip label={`About the ${f.label.toLowerCase()}`}>{f.hint}</InfoTip>
            </div>
            <input id={`post-install-${f.key}`} value={val(f.key)} disabled={!canManage} placeholder={f.placeholder}
              onChange={(e) => { setDraft((d) => ({ ...d, [f.key]: e.target.value })); setSaved(false); }}
              className="w-full rounded-md border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30 disabled:bg-slate-50" />
          </div>
        ))}
      </div>
      {canManage && (
        <div className="mt-3 flex items-center justify-end gap-3">
          {saved && !dirty.length && <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"><Check className="h-3.5 w-3.5" /> Saved</span>}
          {save.isError && <span className="text-xs font-medium text-rose-600">Didn&apos;t save — try again.</span>}
          <button type="button" onClick={() => void onSave()} disabled={!dirty.length || save.isPending}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-40">
            {save.isPending ? "Saving…" : "Save links"}
          </button>
        </div>
      )}
    </div>
  );
}
